const SESSION_KEY = 'landcorp_attribution';

/** Session storage key — shared with early inline capture script. */
export const ATTRIBUTION_STORAGE_KEY = SESSION_KEY;

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  gclid?: string;
};

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
] as const;

/** Query param names persisted for attribution — shared with early inline capture script. */
export const ATTRIBUTION_QUERY_KEYS = ATTRIBUTION_KEYS;

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];

const BODY_KEY_ALIASES: Partial<Record<AttributionKey, readonly string[]>> = {
  utm_source: ['utm_source', 'utmSource'],
};

function trimParam(value: string | null | undefined, max = 200): string | undefined {
  const trimmed = (value ?? '').trim().slice(0, max);
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Parse attribution params from a URL search string. */
export function readAttributionFromSearch(search: string): Attribution {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const out: Attribution = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = trimParam(params.get(key));
    if (value) out[key] = value;
  }

  return out;
}

function hasAttribution(data: Attribution): boolean {
  return ATTRIBUTION_KEYS.some((key) => Boolean(data[key]));
}

function mergeAttribution(base: Attribution, incoming: Attribution): Attribution {
  const out: Attribution = { ...base };
  for (const key of ATTRIBUTION_KEYS) {
    if (incoming[key]) out[key] = incoming[key];
  }
  return out;
}

/** Persist attribution to sessionStorage when present in the URL. */
export function persistAttributionIfPresent(search: string): void {
  if (typeof window === 'undefined') return;

  const incoming = readAttributionFromSearch(search);
  if (!hasAttribution(incoming)) return;

  try {
    const existing = readStoredAttribution();
    const merged = mergeAttribution(existing, incoming);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(merged));
  } catch {
    /* private mode / quota */
  }
}

function readStoredAttribution(): Attribution {
  if (typeof window === 'undefined') return {};

  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    const out: Attribution = {};
    for (const key of ATTRIBUTION_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.trim()) {
        out[key] = value.trim().slice(0, 200);
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Current URL params merged with persisted session attribution. */
export function getAttribution(): Attribution {
  if (typeof window === 'undefined') return {};

  const fromUrl = readAttributionFromSearch(window.location.search);
  const fromSession = readStoredAttribution();
  return mergeAttribution(fromSession, fromUrl);
}

/** Flat attribution object for Vercel event payloads. */
export function attributionToVaData(attribution?: Attribution): Record<string, string | null> {
  const data = attribution ?? getAttribution();
  return {
    utm_source: data.utm_source ?? null,
    utm_medium: data.utm_medium ?? null,
    utm_campaign: data.utm_campaign ?? null,
    utm_content: data.utm_content ?? null,
    utm_term: data.utm_term ?? null,
    fbclid: data.fbclid ?? null,
    gclid: data.gclid ?? null,
  };
}

function sanitizeAttributionParam(value: unknown, max = 200): string | undefined {
  const trimmed = String(value ?? '')
    .replace(/[\r\n\u0000]/g, ' ')
    .trim()
    .slice(0, max);
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Parse attribution fields from a contact API JSON body (server-side). */
export function parseAttributionFromBody(body: Record<string, unknown>): Attribution {
  const out: Attribution = {};

  for (const key of ATTRIBUTION_KEYS) {
    const aliases = BODY_KEY_ALIASES[key] ?? [key];
    for (const alias of aliases) {
      const value = sanitizeAttributionParam(body[alias]);
      if (value) {
        out[key] = value;
        break;
      }
    }
  }

  return out;
}

export function hasAttributionData(attribution: Attribution): boolean {
  return ATTRIBUTION_KEYS.some((key) => Boolean(attribution[key]));
}

/** Body fields for contact form POST. */
export function attributionToFormFields(attribution?: Attribution): Record<string, string> {
  const data = attribution ?? getAttribution();
  const out: Record<string, string> = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = data[key];
    if (value) out[key] = value;
  }

  return out;
}
